import { z } from 'zod';
import { GLYPH } from '@/shared/lib';

/**
 * Ошибка API по ТЗ 3.1.3 (docs/frontend/deltas.md):
 * тело `{"error": {code, message, details, trace_id}}` → ApiError {code, message, details?, status, requestId}.
 */
export const errorEnvelopeSchema = z.object({
  error: z.object({
    code: z.string().min(1),
    message: z.string(),
    details: z.unknown().optional(),
    trace_id: z.string().nullish(),
  }),
});

/** 422 validation_error: `details.fields: [{field, code, message}]`; field — snake_case путь. */
export const apiFieldErrorSchema = z.object({
  field: z.string(),
  code: z.string(),
  message: z.string(),
});
export type ApiFieldError = z.infer<typeof apiFieldErrorSchema>;

const validationDetailsSchema = z.object({ fields: z.array(apiFieldErrorSchema) });
const retryAfterDetailsSchema = z.object({ retry_after_sec: z.number().nonnegative() });

/** Общие коды ТЗ 3.1.3 + клиентские коды (сеть, неожиданный ответ). Коды модулей — строки из ответа. */
export const ApiErrorCode = {
  ValidationError: 'validation_error',
  Unauthorized: 'unauthorized',
  Forbidden: 'forbidden',
  AccountRequired: 'account_required',
  NotFound: 'not_found',
  Conflict: 'conflict',
  RateLimited: 'rate_limited',
  ClientUpdateRequired: 'client_update_required',
  ServiceUnavailable: 'service_unavailable',
  NetworkError: 'network_error',
  UnexpectedResponse: 'unexpected_response',
  /** Запрос отменён клиентом (AbortSignal) — не ошибка для пользователя и не пишется в лог. */
  Aborted: 'aborted',
} as const;
export type ApiErrorCode = (typeof ApiErrorCode)[keyof typeof ApiErrorCode];

export interface ApiErrorInit {
  code: string;
  message: string;
  status: number;
  details?: unknown;
  requestId?: string | null;
  retryAfterSec?: number | null;
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: unknown;
  /** X-Request-Id ответа (или trace_id тела) — по нему ищется серверный лог. */
  readonly requestId: string | null;
  /** Секунды до повтора для 429 (заголовок Retry-After или details.retry_after_sec). */
  readonly retryAfterSec: number | null;

  constructor(init: ApiErrorInit) {
    super(init.message);
    this.name = 'ApiError';
    this.code = init.code;
    this.status = init.status;
    this.details = init.details;
    this.requestId = init.requestId ?? null;
    this.retryAfterSec = init.retryAfterSec ?? readRetryAfterFromDetails(init.details);
  }

  /** Ошибки полей 422 (пусто для остальных ошибок). */
  get fieldErrors(): ApiFieldError[] {
    const parsed = validationDetailsSchema.safeParse(this.details);
    return parsed.success ? parsed.data.fields : [];
  }
}

function readRetryAfterFromDetails(details: unknown): number | null {
  const parsed = retryAfterDetailsSchema.safeParse(details);
  return parsed.success ? parsed.data.retry_after_sec : null;
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/** Запрос отменён клиентом (замена файла, закрытие окна) — показывать нечего. */
export function isAbortError(error: unknown): boolean {
  return isApiError(error) && error.code === ApiErrorCode.Aborted;
}

/**
 * Ошибка проверки полей: 422 с `details.fields` — и `validation_error`, и коды модулей, привязанные
 * к полю (например, `wrong_current_password` → `current_password`, ТЗ 4.8.1), а также `409 conflict`
 * с `details.fields` — дубликат значения поля (GEO 5.8.2: «Такое обозначение уже есть в справочнике»).
 */
export function hasFieldErrors(error: unknown): error is ApiError {
  return (
    isApiError(error) &&
    (error.status === 422 || error.status === 409) &&
    error.fieldErrors.length > 0
  );
}

/**
 * Тексты ошибок полей одной строкой (тост «нельзя отправить на модерацию: …»): без повторов; фраза
 * с точкой в конце отделяется пробелом, короткая формулировка без точки — « · ». `labelOf` — подпись
 * поля к общему тексту сервера («Поле обязательно» → «Старт продаж — Поле обязательно»).
 */
export function fieldErrorsText(
  fields: readonly ApiFieldError[],
  labelOf: (field: string) => string | undefined = () => undefined,
): string {
  const messages = new Set<string>();
  for (const { field, message } of fields) {
    const text = message.trim();
    if (!text) continue;
    const label = labelOf(field);
    messages.add(label ? `${label} — ${text}` : text);
  }
  let line = '';
  for (const message of messages) {
    const separator = /[.!?…]$/u.test(line) ? ' ' : ` ${GLYPH.middot} `;
    line = line ? `${line}${separator}${message}` : message;
  }
  return line;
}

const singleFieldDetailsSchema = z.object({ field: z.string().min(1) });

/**
 * Ошибка под поля формы раздела: пути полей сервера переименовываются (`price.amount` → `price`), а
 * ошибка `422`/`409` без `details.fields`, но с `details.field` (например, `422 capacity_below_taken`
 * «Мест меньше, чем уже записано»), становится ошибкой этого поля с текстом сервера. Остальные
 * ошибки возвращаются как есть.
 */
export function remapFieldErrors(error: unknown, rename: (field: string) => string): unknown {
  if (!isApiError(error)) return error;
  const fields = error.fieldErrors;
  let mapped: ApiFieldError[] | null = null;
  if (fields.length > 0) {
    mapped = fields.map((item) => ({ ...item, field: rename(item.field) }));
  } else if (error.status === 422 || error.status === 409) {
    const single = singleFieldDetailsSchema.safeParse(error.details);
    if (single.success) {
      mapped = [{ field: rename(single.data.field), code: error.code, message: error.message }];
    }
  }
  if (!mapped) return error;
  return new ApiError({
    code: error.code,
    message: error.message,
    status: error.status,
    requestId: error.requestId,
    details: { fields: mapped },
  });
}

/** Код для случаев, когда тело ответа не соответствует конверту ТЗ (например, HTML от прокси). */
export function codeFromStatus(status: number): string {
  switch (status) {
    case 401:
      return ApiErrorCode.Unauthorized;
    case 403:
      return ApiErrorCode.Forbidden;
    case 404:
      return ApiErrorCode.NotFound;
    case 409:
      return ApiErrorCode.Conflict;
    case 422:
      return ApiErrorCode.ValidationError;
    case 429:
      return ApiErrorCode.RateLimited;
    case 502:
    case 503:
    case 504:
      return ApiErrorCode.ServiceUnavailable;
    default:
      return ApiErrorCode.UnexpectedResponse;
  }
}

/** Retry-After: секунды или HTTP-дата (RFC 9110). */
export function parseRetryAfter(value: unknown, now: number = Date.now()): number | null {
  if (typeof value !== 'string' || value.trim() === '') return null;
  const seconds = Number(value);
  if (Number.isFinite(seconds)) return Math.max(0, Math.ceil(seconds));
  const date = Date.parse(value);
  return Number.isNaN(date) ? null : Math.max(0, Math.ceil((date - now) / 1000));
}

/** Код клиента: провайдер интеграции недоступен или не настроен — показывается текст сервера. */
export const PROVIDER_UNAVAILABLE = 'provider_unavailable';

/**
 * Ошибка провайдера интеграции (`502`/`503`: «Видеохостинг не настроен…», «DHL не отвечает…»): общий
 * код `service_unavailable` заменяется кодом клиента, чтобы окна и тосты показали объяснение сервера,
 * а не общий текст «Сервис временно недоступен» (ТЗ: интеграция не подключена — честно, текстом).
 */
export function providerError(error: unknown): unknown {
  if (
    error instanceof ApiError &&
    (error.status === 502 || error.status === 503) &&
    error.message &&
    !error.message.startsWith('HTTP ')
  ) {
    return new ApiError({
      code: PROVIDER_UNAVAILABLE,
      message: error.message,
      status: error.status,
      details: error.details,
      requestId: error.requestId,
      retryAfterSec: error.retryAfterSec,
    });
  }
  return error;
}

/** Запрос к операции, за которой стоит провайдер: его `502`/`503` — с текстом сервера. */
export async function viaProvider<T>(request: Promise<T>): Promise<T> {
  try {
    return await request;
  } catch (error) {
    throw providerError(error);
  }
}
